/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

/**
 * @modified Amar Tsolmon
 * @version Material Datepicker version 10.0.1 source modified to work with
 * Material version 5.0.0-rc.2 and Angular version 5.1.0
 */

import {UP_ARROW, DOWN_ARROW} from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import {MatDatepickerBase, MatDatepickerControl} from './datepicker-base';
import {MAT_SINGLE_DATE_SELECTION_MODEL_PROVIDER} from '@angular/material/datepicker';

/**
 * Non-Material version 10.0.1 feature: Datepicker Time
 * Datepicker Time Feature Constants
 */
export enum TimeModelEnum {
  HOUR = 'hour',
  MINUTE = 'minute',
  SECOND = 'second'
}
export enum MeridianEnum {
  AM = 'AM',
  PM = 'PM'
}

const { HOUR, MINUTE } = TimeModelEnum;
const { AM, PM } = MeridianEnum;

/* tslint:disable */

@Component({
  selector: 'mat-datepicker',
  template: '',
  exportAs: 'matDatepicker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [MAT_SINGLE_DATE_SELECTION_MODEL_PROVIDER]
})
export class MatDatepicker<D> extends MatDatepickerBase<MatDatepickerControl<D>, D | null, D> {
  
  /**
   * Start of:
   * Non-Material version 10.0.1 feature: Datepicker Month Year
   * Datepicker Month Year Feature properties
   */
  /**
   * @description Determines whether month only selection should be used instead of days.
   */
  @Input() public monthMode: boolean;
  /**
   * End of:
   * Non-Material version 10.0.1 feature: Datepicker Month Year
   * Datepicker Month Year Feature properties
   */
  
  /**
   * Start of:
   * Non-Material version 10.0.1 feature: Datepicker Time
   * Datepicker Time Feature properties
   */
  /**
   * @description Whether to include the time parts of the date when doing comparisons. Setting this to 'true'
   * will render datepicker content time editor inputs and enables action mode.
   */
  @Input() includeTime: boolean;
  /**
   * @description Whether to include the seconds of the date on time mode calendar and when doing comparisons.
   * @default true
   */
  @Input() includeSeconds: boolean = true;
  /**
   * @description Whether to use AM/PM time format on time mode calendar. Setting this to 'true' will render
   * datepicker content AM/PM toggle button.
   */
  @Input() meridianMode: boolean;
  /**
   * @description Whether to disable the minutes of the date on time mode calendar.
   */
  @Input() disableMinute: boolean;
  /**
   * @description Time part increment step sizes.
   * @default Defaults to 1 upon change.
   */
  @Input() stepHour: number;
  @Input() stepMinute: number;
  @Input() stepSecond: number;
  public hourValue: any;
  public minuteValue: any;
  public secondValue: any;
  public meridianValue: MeridianEnum = AM;
  /**
   * End of:
   * Non-Material version 10.0.1 feature: Datepicker Time
   * Datepicker Time Feature properties
   */

  /**
   * @description Open the calendar.
   * @override
   * */
  open(): void {
    super.open();
    /**
     * Non-Material version 10.0.1 feature: Datepicker Time
     * If the datepicker uses time picker then set the initial time models from the selection on calendar open
     */
    if (this.includeTime && this.opened) {
      this.updateHourMinuteSecond();
    }
  }
  
  /**
   * @description Emits selected month in year view.
   * @override
   * */
  _selectMonth(normalizedMonth: D): void {
    if (this.monthMode) {
      /**
       * Non-Material version 10.0.1 feature: Datepicker Actions, Datepicker Month Year
       * If the datepicker uses action mode and month mode then do not trigger model selectionChanged event
       * just update the model selection by sending datepicker-input as the source.
       */
      if (this.actionMode && (this as any)._model) {
        (this as any)._model.updateSelection(normalizedMonth, this._datepickerInput);
      } else {
        this.select(normalizedMonth);
        this.close();
      }
    }
    super._selectMonth(normalizedMonth);
  }
  
  /**
   * Start of:
   * Non-Material version 10.0.1 feature: Datepicker Time
   * Datepicker Time Feature methods
   */
  /**
   * @description Formats the time input value, accepting only numeric inputs.
   * @param input
   */
  public formatInputValue(input: HTMLInputElement): void {
    input.value = input.value.replace(/[^0-9]/g, '');
  }
  
  public toggleMeridian(ngModelRef: NgModel): void {
    this.meridianValue = this.meridianValue === AM ? PM : AM;
    this.changeTimeModel(HOUR, ngModelRef);
  }
  
  public onTimeModelChange(type: TimeModelEnum, newValue: any): void {
    if (type === HOUR) {
      this.hourValue = newValue;
    } else if (type === MINUTE) {
      this.minuteValue = newValue;
    } else {
      this.secondValue = newValue;
    }
    this.updateModel();
  }
  
  /**
   * @description Handles keydown events on the datepicker time input.
   * @param event
   * @param type
   * @param ngModelRef
   */
  public onTimeInputKeydown(
    event: KeyboardEvent,
    type: TimeModelEnum,
    ngModelRef: NgModel
  ): void {
    if (event) {
      switch (event.keyCode) {
        case UP_ARROW:
          this.changeTimeModel(type, ngModelRef, true);
          event.preventDefault();
          break;
        case DOWN_ARROW:
          this.changeTimeModel(type, ngModelRef, false);
          event.preventDefault();
          break;
      }
    }
  }
  
  /**
   * @description Changes the property of a time model.
   * @param type
   * @param ngModelRef
   * @param isIncrement
   */
  public changeTimeModel(type: TimeModelEnum, ngModelRef: NgModel, isIncrement?: boolean): void {
    const nextValue: number = this.getNextTimeModelValue(type, isIncrement);
    
    if (ngModelRef && ngModelRef.control) {
      ngModelRef.control.setValue(this.formatTwoDigitTimeValue(nextValue), { onlySelf: false, emitEvent: false });
      this.updateModel();
    }
  }
  
  /**
   * @description Updates the hour, minute, and second time models on datepicker selected date is set.
   */
  public updateHourMinuteSecond(): void {
    let hour: number = (this as any)._dateAdapter.getHour((this as any)._model.selection);
    const minute: number = (this as any)._dateAdapter.getMinute((this as any)._model.selection);
    const second: number = (this as any)._dateAdapter.getSecond((this as any)._model.selection);
    
    if (this.meridianMode) {
      if (hour > 12) {
        hour = hour - 12;
        this.meridianValue = PM;
      } else {
        this.meridianValue = AM;
      }
    }
    this.hourValue = this.formatTwoDigitTimeValue(hour);
    this.minuteValue = this.formatTwoDigitTimeValue(minute);
    this.secondValue = this.formatTwoDigitTimeValue(second);
  }
  
  /**
   * @description Updates the datepicker selection with the current time model values.
   */
  public updateModel(): void {
    let hour: number = this.getValidTimeValue(this.hourValue);
    if (this.meridianMode && this.meridianValue === PM && hour !== 12) {
      hour = hour + 12;
    }
    (this as any)._dateAdapter.setHour((this as any)._model.selection, hour);
    (this as any)._dateAdapter.setMinute((this as any)._model.selection, this.getValidTimeValue(this.minuteValue));
    (this as any)._dateAdapter.setSecond((this as any)._model.selection, this.getValidTimeValue(this.secondValue));
  }
  
  /**
   * @description Gets the next time model value by type.
   * @param type
   * @param isIncrement
   * @returns {number}
   */
  public getNextTimeModelValue(type: TimeModelEnum, isIncrement?: boolean): number {
    const min: number = 0;
    let max: number;
    let nextValue: number;
    let stepSize: number;
    let timeModel: number;
    
    if (type === HOUR) {
      timeModel = this.getValidTimeValue(this.hourValue);
      stepSize = this.stepHour || 1;
      max = this.meridianMode ? 12 : 24;
    } else if (type === MINUTE) {
      timeModel = this.getValidTimeValue(this.minuteValue);
      stepSize = this.stepMinute || 1;
      max = 60;
    } else {
      timeModel = this.getValidTimeValue(this.secondValue);
      stepSize = this.stepSecond || 1;
      max = 60;
    }
    if (isIncrement === null || isIncrement === undefined) {
      nextValue = timeModel % (max);
    } else {
      nextValue = isIncrement ? timeModel + stepSize : timeModel - stepSize;
      if (type === HOUR && this.meridianMode) {
        nextValue = nextValue % (max + 1);
        if (nextValue === 0) {
          nextValue = isIncrement ? 1 : max;
        }
      } else {
        nextValue = nextValue % max;
      }
      if (isIncrement) {
        nextValue = nextValue > max ? (nextValue - max + min) : nextValue;
      } else {
        nextValue = nextValue < min ? (nextValue - min + max) : nextValue;
      }
    }
    return nextValue;
  }
  
  public getValidTimeValue(timeModel: any): number {
    return isNaN(Number(timeModel)) ? 0 : Number(timeModel);
  }
  
  public formatTwoDigitTimeValue(value: number): string {
    const txt: string = (value !== null && value !== undefined) ? value.toString() : '0';
    return txt.length > 1 ? txt : `0${txt}`;
  }
  /**
   * End of:
   * Non-Material version 10.0.1 feature: Datepicker Time
   * Datepicker Time Feature methods
   */
}
/* tslint:enable */
